import React from "react";
import { Link } from "react-router-dom";
import { Shield, Lock, Eye, Database, UserCheck, FileCheck, Server, ArrowLeft } from "lucide-react";

const CONTROLS = [
  { icon: Lock, title: "Encryption Everywhere", body: "All data is encrypted in transit (TLS 1.2+) and at rest. Policyholder data, photos, and reports are never stored or transmitted unencrypted." },
  { icon: Database, title: "Carrier Data Isolation", body: "Strict multi-tenant architecture — every query is scoped to your carrier. Your data is never visible to, or commingled with, any other carrier on the platform." },
  { icon: UserCheck, title: "MFA & Role-Based Access", body: "Multi-factor authentication for staff accounts, granular role-based permissions (admin, underwriter, loss control, broker), and row-level security for field staff." },
  { icon: Eye, title: "Comprehensive Audit Logging", body: "Every data access, report action, recommendation closure, and configuration change is logged with actor, timestamp, and IP — reviewable at any time." },
  { icon: Server, title: "Monitored Infrastructure", body: "Real-time error monitoring, deep health checks with database connectivity verification, and uptime alerting on our production environment." },
  { icon: FileCheck, title: "Your Data Stays Yours", body: "Carrier data is never used to train public AI models. Configurable retention, full export, and deletion on offboarding — backed by contractual data processing terms." },
];

export default function TrustPage() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="trust-page">
      <header className="bg-navy px-6 py-16">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to RiskSurvey.ai
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/15 rounded-xl flex items-center justify-center">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white">Trust & Security</h1>
          </div>
          <p className="text-white/70 text-lg max-w-2xl">
            Carriers hold sensitive policyholder data to the highest standard. So do we.
            RiskSurvey.ai is built with SOC 2-aligned controls by design — and audit-committed by contract.
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {CONTROLS.map(c => (
            <div key={c.title} data-testid={`trust-control-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
              <div className="w-10 h-10 bg-navy/10 rounded-lg flex items-center justify-center mb-3">
                <c.icon className="w-5 h-5 text-navy" />
              </div>
              <h2 className="font-heading text-lg font-bold text-slate-800 mb-1.5">{c.title}</h2>
              <p className="text-sm text-slate-500 leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 bg-white border border-slate-200 rounded-2xl p-6">
          <h2 className="font-heading text-lg font-bold text-slate-800 mb-2">Compliance Roadmap</h2>
          <p className="text-sm text-slate-500 leading-relaxed">
            Our security program is aligned to the SOC 2 Trust Services Criteria today, with written information
            security, access control, and incident response policies in force. We contractually commit to completing
            a SOC 2 Type I examination in our master service agreements, and we respond to carrier security
            questionnaires (SIG Lite / CAIQ) within two business days.
          </p>
          <p className="text-sm text-slate-500 mt-3">
            Security questions or vendor review requests: <span className="text-navy font-medium">security@risksurvey.ai</span>
          </p>
        </div>
      </main>

      <footer className="text-center text-xs text-slate-400 pb-10">© 2026 RiskSurvey.ai — All rights reserved</footer>
    </div>
  );
}
